import React, { useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Loader2, BookOpen, Lightbulb, AlertCircle, ChevronRight, Sparkles } from "lucide-react";
import AppellationsInText from "@/components/AppellationsInText";

const SUGGESTED = ["Le terroir", "Sulfites dans le vin", "Fermentation malolactique", "Pinot Noir vs Pinot Meunier", "Botrytis cinerea", "Élevage en amphore"];
const LEVELS = [{ v: "debutant", l: "Découverte" }, { v: "amateur", l: "Amateur" }, { v: "professionnel", l: "Pro" }, { v: "expert", l: "Expert" }];

export default function LearnExplain() {
  const { lang } = useT();
  const [topic, setTopic] = useState("");
  const [level, setLevel] = useState("amateur");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);

  const run = async (q) => {
    const t = q || topic;
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await api.post("/tlp/learn/explain", { topic: t, level, lang });
      setOut(data.explanation);
      if (q) setTopic(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="learn-explain">
      <span className="chip-pink inline-flex items-center gap-1.5"><BookOpen className="w-3 h-3" /> Learn · Explain</span>
      <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">Demandez à geoVINUM AI</h1>
      <p className="text-[#4A5568] mt-3 max-w-2xl">Posez n'importe quelle question viticole — geoVINUM AI structure la réponse en cartes thématiques avec exemple concret et erreurs courantes.</p>

      <div className="mt-8 app-card p-6">
        <div className="flex gap-2 flex-wrap mb-4">
          {LEVELS.map((l) => (
            <button key={l.v} onClick={() => setLevel(l.v)} data-testid={`level-${l.v}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${level === l.v ? "bg-[#E5276A] text-white border-[#E5276A]" : "bg-white text-[#1F3A5F] border-[#1F3A5F]/15"}`}>
              {l.l}
            </button>
          ))}
        </div>
        <textarea value={topic} onChange={(e) => setTopic(e.target.value)}
          rows={2} data-testid="explain-topic"
          placeholder="Ex : Qu'est-ce que la pourriture noble ? Comment fonctionne l'élevage en barrique ?"
          className="w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-4 py-3 text-sm text-[#1F3A5F]" />
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button onClick={() => run()} disabled={busy || !topic} data-testid="explain-submit"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Expliquer
          </button>
          <span className="text-xs text-[#8B95A1]">Gratuit en niveau Découverte (limité) · 3 crédits sur les plans payants</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {SUGGESTED.map((s) => (
            <button key={s} onClick={() => run(s)} className="text-xs px-3 py-1.5 rounded-full bg-[#FBF7F3] text-[#4A5568] hover:bg-[#FCE4ED] hover:text-[#E5276A] border border-[#1F3A5F]/8">
              {s}
            </button>
          ))}
        </div>
      </div>

      {out && (
        <div className="mt-10 space-y-6" data-testid="explain-output">
          <div>
            <h2 className="font-display text-3xl text-[#1F3A5F]">{out.title}</h2>
            {out.hook && <p className="mt-2 text-base text-[#E5276A] italic">{out.hook}</p>}
          </div>
          {out.definition && (
            <div className="app-card p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">Définition</div>
              <p className="text-[#1F3A5F] leading-relaxed">{out.definition}</p>
            </div>
          )}
          {out.cards?.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4">
              {out.cards.map((c, i) => (
                <div key={i} className="app-card p-5" data-testid={`explain-card-${i}`}>
                  <h3 className="font-bold text-[#1F3A5F] inline-flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> {c.label}</h3>
                  <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
          )}
          {out.example && (
            <div className="rounded-2xl bg-[#FBF7F3] border-l-4 border-[#E5276A] p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-1">Exemple</div>
              <h3 className="font-bold text-[#1F3A5F]">{out.example.name}</h3>
              <p className="text-sm text-[#4A5568] mt-1">{out.example.why}</p>
            </div>
          )}
          {out.common_mistake && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-sm text-[#7c5f00]"><b>Idée reçue :</b> {out.common_mistake}</p>
            </div>
          )}
          {out.next_steps?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">Pour aller plus loin</div>
              <ul className="space-y-2">
                {out.next_steps.map((s, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[#1F3A5F]"><ChevronRight className="w-4 h-4 text-[#E5276A] mt-0.5 shrink-0" />{s}</li>
                ))}
              </ul>
            </div>
          )}
          <AppellationsInText text={[
            out.title, out.hook, out.definition,
            ...(out.cards || []).map((c) => `${c.label} ${c.body}`),
            out.example?.name, out.example?.why,
            out.common_mistake,
            ...(out.next_steps || []),
          ].filter(Boolean).join(" ")} />
        </div>
      )}
    </div>
  );
}
